import { managementDraft, readManagement, managementSaved } from '../utils/managementDraft';
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Checkbox,
  MenuItem,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faComments,
  faHeadset,
  faEnvelope,
  faDesktop,
  faUserPlus,
  faPlus,
  faTrash,
  faTriangleExclamation,
  faGaugeHigh,
} from "@fortawesome/free-solid-svg-icons";
import {
  faSlack,
  faMicrosoft,
  faTelegram,
} from "@fortawesome/free-brands-svg-icons";
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from "./BrandIcons";
import { authFetch } from "../utils/api";
import { guardWrite } from "../utils/permissions";
import { useTranslation } from "react-i18next";
import "../translations.js";
import "./liveChatSettings.css";
import "./notificationCenter.css";
import TelegramLinker from "./TelegramLinker";
import SlackNotificationLinker from "./SlackNotificationLinker";
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext.js";

// --------------------------------------------------------------------------
// Channels & sections (UI metadata only — backend is the source of truth
// for which channels exist).
// --------------------------------------------------------------------------
// Per-channel header descriptors. Slack/Teams/Telegram use inline
// multi-color brand SVGs (see ./BrandIcons) because FontAwesome's brand
// glyphs are monochrome and can't reproduce the official marks. Their
// `color` field is still kept so any other usages (recipient block
// headings, error icons) stay on-brand.
const NOTIFICATION_CHANNELS = [
  { key: "in_app",   icon: faDesktop,    iconType: "solid",  color: "#5616ea", labelKey: "live_notify_pref_channel_in_app" },
  { key: "email",    icon: faEnvelope,   iconType: "solid",  color: "#5616ea", labelKey: "live_notify_pref_channel_email" },
  { key: "telegram", icon: faTelegram,   iconType: "brand",  color: "#229ED9", labelKey: "live_notify_pref_channel_telegram", BrandIcon: TelegramBrandIcon },
  { key: "slack",    icon: faSlack,      iconType: "brand",  color: "#E01E5A", labelKey: "live_notify_pref_channel_slack",    BrandIcon: SlackBrandIcon },
  { key: "teams",    icon: faMicrosoft,  iconType: "brand",  color: "#5059C9", labelKey: "live_notify_pref_channel_teams",    BrandIcon: TeamsBrandIcon,    comingSoon: true },
];

const NOTIFICATION_SECTIONS = [
  {
    key: "live_chat",
    titleKey: "notify_section_live_chat",
    tooltipKey: "notify_section_live_chat_tooltip",
    events: [
      { key: "new_chat",       icon: faComments,     titleKey: "live_notify_pref_event_new_chat",       descKey: "live_notify_pref_event_new_chat_desc" },
      { key: "help_requested", icon: faHeadset, titleKey: "live_notify_pref_event_help_requested", descKey: "live_notify_pref_event_help_requested_desc", highlight: true },
      { key: "new_message",    icon: faEnvelope,     titleKey: "live_notify_pref_event_new_message",    descKey: "live_notify_pref_event_new_message_desc" },
    ],
  },
  {
    key: "leads",
    titleKey: "notify_section_leads",
    tooltipKey: "notify_section_leads_tooltip",
    events: [
      { key: "new_lead", icon: faUserPlus, titleKey: "notify_event_new_lead", descKey: "notify_event_new_lead_desc" },
    ],
  },
  {
    key: "usage",
    titleKey: "notify_section_usage",
    tooltipKey: "notify_section_usage_tooltip",
    events: [
      { key: "usage_warning", icon: faGaugeHigh, titleKey: "notify_event_usage_warning", descKey: "notify_event_usage_warning_desc" },
    ],
  },
];

const ALL_EVENT_KEYS = NOTIFICATION_SECTIONS.flatMap((s) => s.events.map((e) => e.key));
const ALL_SECTION_KEYS = NOTIFICATION_SECTIONS.map((s) => s.key);
// Flat list of every event across the backend sections — the UI shows a
// single unified matrix ("Notifications") regardless of which backend
// section an event belongs to. `new_message` is pinned near the end because
// it is the highest-frequency event; `usage_warning` sits last because it is
// about the plan, not about a conversation.
const ALL_EVENTS = (() => {
  const flat = NOTIFICATION_SECTIONS.flatMap((s) => s.events);
  const pinnedTail = ["new_message", "usage_warning"];
  const head = flat.filter((e) => !pinnedTail.includes(e.key));
  const tail = pinnedTail
    .map((key) => flat.find((e) => e.key === key))
    .filter(Boolean);
  return [...head, ...tail];
})();
const DEFAULT_PREFS = {
  new_chat:       { in_app: true,  email: false, slack: false, teams: false, telegram: false },
  help_requested: { in_app: true,  email: true,  slack: false, teams: false, telegram: false },
  new_message:    { in_app: true,  email: false, slack: false, teams: false, telegram: false },
  new_lead:       { in_app: true,  email: true,  slack: false, teams: false, telegram: false },
  usage_warning:  { in_app: true,  email: true,  slack: false, teams: false, telegram: false },
};

// usage_warning threshold: percent of the monthly AI-response allowance at
// which the alert fires. Mirrors the backend clamp range (50..95).
const USAGE_THRESHOLD_DEFAULT = 80;
const USAGE_THRESHOLD_OPTIONS = [50, 80, 90, 95];

// Channels that are not allowed for a given event. `new_message` fires
// for every visitor reply in a joined chat, so high-friction channels stay
// locked. Telegram is allowed because operators can use it as a real-time
// companion channel while actively handling a conversation.
const LOCKED_CHANNELS = {
  new_message: ["email", "slack", "teams"],
};
const isChannelLocked = (eventKey, channelKey) =>
  (LOCKED_CHANNELS[eventKey] || []).includes(channelKey);

// --------------------------------------------------------------------------
// Recipient channel descriptors: every text-input-driven recipient channel
// shares the same UI shape, so we describe them as data and render them
// generically below. Slack uses its own bot-token + channel-picker flow
// (SlackNotificationLinker) and Telegram uses its deep-link flow
// (TelegramLinker) — both are special-cased outside of this list.
// --------------------------------------------------------------------------
const TEAMS_HOST_REGEX = /^https:\/\/([A-Za-z0-9-]+\.webhook\.office\.com|prod-[0-9]+\.[a-z0-9-]+\.logic\.azure\.com)(:443)?\//;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RECIPIENT_CHANNELS = [
  {
    key:               "email",
    stateKey:          "emails",
    payloadKey:        "emails_by_section",
    icon:              faEnvelope,
    iconType:          "solid",
    color:             "#5616ea",
    bgColor:           "rgba(86, 22, 234, 0.10)",
    headingKey:        "notify_section_email_recipients",
    inputLabelKey:     "notify_email_label",
    inputPlaceholderKey: "notify_email_placeholder",
    addButtonKey:      "notify_add_email",
    validationErrorKey: "notify_email_validation_error",
    isValid:           (v) => EMAIL_REGEX.test(String(v || "").trim()),
  },
  {
    key:               "teams",
    stateKey:          "teamsList",
    payloadKey:        "teams_webhooks_by_section",
    icon:              faMicrosoft,
    iconType:          "brand",
    color:             "#5059C9",
    bgColor:           "rgba(80, 89, 201, 0.10)",
    BrandIcon:         TeamsBrandIcon,
    headingKey:        "notify_section_teams_recipients",
    inputLabelKey:     "notify_teams_label",
    inputPlaceholderKey: "notify_teams_placeholder",
    addButtonKey:      "notify_add_teams",
    validationErrorKey: "notify_teams_validation_error",
    isValid:           (v) => TEAMS_HOST_REGEX.test(String(v || "").trim()),
  },
];

const TELEGRAM_PAYLOAD_KEY = "telegram_chat_ids_by_section";
// The backend column name is historical — it no longer holds Incoming
// Webhook URLs but rather "team_id:channel_id" Slack destinations. Renaming
// the column was deemed unnecessary risk for the production migration.
const SLACK_PAYLOAD_KEY = "slack_webhooks_by_section";

// --------------------------------------------------------------------------
// Normalizers — backend stores recipients per-section (`_by_section`) but
// the UI now exposes ONE unified recipient list per channel. We merge all
// section lists into a single deduped array on load, and mirror that array
// back into every section key on save.
// --------------------------------------------------------------------------
const normalizeRecipientList = (raw) => {
  const seen = new Set();
  const out = [];
  if (raw && typeof raw === "object") {
    for (const key of ALL_SECTION_KEYS) {
      const list = Array.isArray(raw[key]) ? raw[key] : [];
      for (const e of list) {
        const s = String(e);
        if (s.length > 0 && !seen.has(s)) {
          seen.add(s);
          out.push(s);
        }
      }
    }
  }
  return out.length > 0 ? out : [""];
};

const normalizeTelegramSelection = (raw) => {
  const seen = new Set();
  const out = [];
  if (raw && typeof raw === "object") {
    for (const key of ALL_SECTION_KEYS) {
      const list = Array.isArray(raw[key]) ? raw[key] : [];
      for (const e of list) {
        const s = String(e);
        if (!seen.has(s)) {
          seen.add(s);
          out.push(s);
        }
      }
    }
  }
  return out;
};

const normalizePrefs = (prefs) => {
  const out = {};
  for (const key of ALL_EVENT_KEYS) {
    const incoming = prefs?.[key] || {};
    const fallback = DEFAULT_PREFS[key];
    out[key] = {};
    for (const ch of NOTIFICATION_CHANNELS) {
      out[key][ch.key] =
        incoming[ch.key] != null ? Boolean(incoming[ch.key]) : Boolean(fallback?.[ch.key]);
    }
  }
  return out;
};

// --------------------------------------------------------------------------
// MUI sx blocks (kept for any future use)
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// Sub-component: per-channel list of text-input recipients (email/slack/teams).
// Now channel-scoped only — there is a single recipient list shared across
// all events that use that channel.
// --------------------------------------------------------------------------
function RecipientListBlock({
  channel,
  values,
  onChange,
  onAdd,
  onRemove,
  saveAttempted,
  t,
}) {
  return (
    <div
      className="notification-emails-section"
      style={{
        "--brand-color": channel.color || "#5616ea",
        "--brand-bg": channel.bgColor || "rgba(86, 22, 234, 0.10)",
      }}
    >
      <div className="notification-emails-section-heading">
        <span className="notification-emails-section-heading-icon">
          {channel.BrandIcon ? (
            <channel.BrandIcon size="18px" />
          ) : (
            <FontAwesomeIcon icon={channel.icon} style={{ color: channel.color || "#5616ea", fontSize: "15px" }} />
          )}
        </span>
        {t(channel.headingKey)}
      </div>
      {channel.key === "email" && (
        <div className="notification-center-warning-inner" style={{ marginTop: 4 }}>
          <FontAwesomeIcon className="notification-center-warning-icon" icon={faTriangleExclamation} />
          <p>
            {t("notify_spam_warning_prefix") ||
              "Missing email alerts? Check spam and add"}{" "}
            <strong>build@ultimo-bots.com</strong>{" "}
            {t("notify_spam_warning_suffix") || "to safe senders."}
          </p>
        </div>
      )}
      <div className="welcome-messages-parent">
        {(values.length > 0 ? values : [""]).map((value, idx) => {
          const list = values.length > 0 ? values : [""];
          const trimmed = String(value).trim();
          const showError = saveAttempted && trimmed.length > 0 && !channel.isValid(trimmed);
          return (
            <Box key={idx} marginBottom={-0.8}>
              <TextField
                label={`${t(channel.inputLabelKey) || channel.key} ${idx + 1}`}
                value={value}
                onChange={(e) => onChange(idx, e.target.value)}
                type="text"
                fullWidth
                variant="outlined"
                error={showError}
                placeholder={t(channel.inputPlaceholderKey) || ""}
                InputProps={{
                  endAdornment:
                    list.length > 1 ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t("remove") || "Remove"}
                          onClick={() => onRemove(idx)}
                          edge="end"
                          size="small"
                          sx={{
                            color: "text.secondary",
                            fontSize: "14px",
                            "& svg": { fontSize: "14px" },
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": { border: "1px solid #515151" },
                  "& .MuiInputLabel-root": {
                    color: "gray",
                    "&.Mui-focused": { color: "#5616ea" },
                  },
                }}
              />
            </Box>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => onAdd()}
        className="add-email-button"
        title={t(channel.addButtonKey) || t("add") || "Add"}
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>
    </div>
  );
}

// --------------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------------
const NotificationCenter = ({ botId, onUnsavedStateChange, onUnsavedHandlersChange }) => {
  const { t } = useTranslation();
  const management = useRef(managementDraft());
  const { registerUnsavedChangesGuard } = useUnsavedChanges();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [prefs, setPrefs] = useState(normalizePrefs({}));
  const [initialPrefs, setInitialPrefs] = useState(normalizePrefs({}));

  // Recipient state — one flat list per RECIPIENT_CHANNELS entry.
  // Shape: { [channelKey]: ["a@b.com", ...] }
  const [recipients, setRecipients] = useState(() =>
    RECIPIENT_CHANNELS.reduce((acc, ch) => ({ ...acc, [ch.key]: [""] }), {})
  );
  const [initialRecipients, setInitialRecipients] = useState(() =>
    RECIPIENT_CHANNELS.reduce((acc, ch) => ({ ...acc, [ch.key]: [""] }), {})
  );

  // Telegram now also a flat list of selected chat_ids (deduped across
  // backend section keys on load, mirrored to all on save).
  const [telegramSelection, setTelegramSelection] = useState([]);
  const [initialTelegramSelection, setInitialTelegramSelection] = useState([]);
  const [telegramLinks, setTelegramLinks] = useState([]); // [{id, chat_id, display_name, ...}]

  // Slack destinations — flat list of "team_id:channel_id" strings, same
  // dedup-on-load / mirror-on-save semantics as telegramSelection.
  const [slackSelection, setSlackSelection] = useState([]);
  const [initialSlackSelection, setInitialSlackSelection] = useState([]);

  // usage_warning threshold (percent of the monthly AI-response allowance).
  const [usageThreshold, setUsageThreshold] = useState(USAGE_THRESHOLD_DEFAULT);
  const [initialUsageThreshold, setInitialUsageThreshold] = useState(USAGE_THRESHOLD_DEFAULT);

  const [saveAttempted, setSaveAttempted] = useState(false);

  // ----- Fetchers ---------------------------------------------------------
  const fetchPrefs = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await readManagement(authFetch, 'notifications', id, 'notification_settings');
      if (!res || !res.ok) throw new Error(`HTTP error! Status: ${res?.status}`);
      const data = await res.json();
      management.current.loaded(id, data);
      const normalizedPrefs = normalizePrefs(data.prefs);
      setPrefs(normalizedPrefs);
      setInitialPrefs(normalizedPrefs);

      const nextRecipients = {};
      for (const ch of RECIPIENT_CHANNELS) {
        nextRecipients[ch.key] = normalizeRecipientList(data[ch.payloadKey]);
      }
      setRecipients(nextRecipients);
      setInitialRecipients(nextRecipients);

      const tg = normalizeTelegramSelection(data[TELEGRAM_PAYLOAD_KEY]);
      setTelegramSelection(tg);
      setInitialTelegramSelection(tg);

      // Slack destinations re-use the same dedup-across-sections helper.
      const sl = normalizeTelegramSelection(data[SLACK_PAYLOAD_KEY]);
      setSlackSelection(sl);
      setInitialSlackSelection(sl);

      const threshold = Number(data.usage_warn_threshold) || USAGE_THRESHOLD_DEFAULT;
      setUsageThreshold(threshold);
      setInitialUsageThreshold(threshold);

      setHasLoadedInitial(true);
    } catch (err) {
      console.error("Error fetching notification prefs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTelegramLinks = useCallback(async () => {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}notifications/telegram/links`
      );
      if (!res || !res.ok) return;
      const data = await res.json();
      setTelegramLinks(Array.isArray(data?.links) ? data.links : []);
    } catch (err) {
      console.error("Error fetching telegram links:", err);
    }
  }, []);

  useEffect(() => {
    if (botId) {
      fetchPrefs(botId);
      fetchTelegramLinks();
    }
  }, [botId, fetchPrefs, fetchTelegramLinks]);

  // (Accordion state removed — the UI now renders a single unified
  // notifications matrix instead of two separate sections.)

  // ----- Pref toggle ------------------------------------------------------
  const togglePref = (eventKey, channelKey) => {
    setSaveAttempted(false);
    setPrefs((prev) => {
      const next = { ...prev };
      const row = { ...(next[eventKey] || {}) };
      row[channelKey] = !row[channelKey];
      next[eventKey] = row;
      return next;
    });
  };

  // ----- Recipient handlers (channel-scoped only) ------------------------
  const updateRecipient = (channelKey) => (idx, value) => {
    setSaveAttempted(false);
    setRecipients((prev) => {
      const list = [...((prev[channelKey]) || [""])];
      list[idx] = value;
      return { ...prev, [channelKey]: list };
    });
  };
  const addRecipient = (channelKey) => () => {
    setSaveAttempted(false);
    setRecipients((prev) => {
      const list = [...((prev[channelKey]) || [""]), ""];
      return { ...prev, [channelKey]: list };
    });
  };
  const removeRecipient = (channelKey) => (idx) => {
    setSaveAttempted(false);
    setRecipients((prev) => {
      const list = (prev[channelKey] || []).filter((_, i) => i !== idx);
      return { ...prev, [channelKey]: list.length > 0 ? list : [""] };
    });
  };

  // ----- Telegram selection toggle ---------------------------------------
  // TelegramLinker still calls `onToggleChat(sectionKey, chatId)` but we
  // ignore the section now — there's a single shared selection.
  const toggleTelegramChat = (_sectionKey, chatId) => {
    setSaveAttempted(false);
    setTelegramSelection((prev) => {
      const list = prev.slice();
      const idx = list.indexOf(chatId);
      if (idx === -1) list.push(chatId);
      else list.splice(idx, 1);
      return list;
    });
  };

  const toggleSlackDestination = (destinationKey) => {
    setSaveAttempted(false);
    setSlackSelection((prev) => {
      const list = prev.slice();
      const idx = list.indexOf(destinationKey);
      if (idx === -1) list.push(destinationKey);
      else list.splice(idx, 1);
      return list;
    });
  };

  // ----- Diff -------------------------------------------------------------
  const hasUnsavedChanges = useMemo(() => {
    if (!hasLoadedInitial) return false;
    if (JSON.stringify(prefs) !== JSON.stringify(initialPrefs)) return true;
    for (const ch of RECIPIENT_CHANNELS) {
      if (JSON.stringify(recipients[ch.key]) !== JSON.stringify(initialRecipients[ch.key])) return true;
    }
    if (JSON.stringify(telegramSelection) !== JSON.stringify(initialTelegramSelection)) return true;
    if (JSON.stringify(slackSelection) !== JSON.stringify(initialSlackSelection)) return true;
    if (usageThreshold !== initialUsageThreshold) return true;
    return false;
  }, [hasLoadedInitial, prefs, initialPrefs, recipients, initialRecipients, telegramSelection, initialTelegramSelection, slackSelection, initialSlackSelection, usageThreshold, initialUsageThreshold]);

  // ----- Validation -------------------------------------------------------
  // Per channel: if at least one (unlocked) event uses the channel, that
  // channel needs at least one valid recipient. Any non-empty entry must
  // additionally pass the channel's format check.
  const channelErrors = useMemo(() => {
    const errs = {}; // { [channelKey]: 'empty'|'invalid' }
    for (const ch of RECIPIENT_CHANNELS) {
      const required = ALL_EVENTS.some(
        (ev) => !isChannelLocked(ev.key, ch.key) && Boolean(prefs[ev.key]?.[ch.key])
      );
      const list = (recipients[ch.key] || []).map((e) => String(e).trim());
      const nonEmpty = list.filter((e) => e.length > 0);
      const hasInvalid = nonEmpty.some((e) => !ch.isValid(e));
      if (required && nonEmpty.length === 0) errs[ch.key] = "empty";
      else if (hasInvalid) errs[ch.key] = "invalid";
    }
    // Telegram: if enabled in any event, need at least one selected chat.
    const tgRequired = ALL_EVENTS.some(
      (ev) => !isChannelLocked(ev.key, "telegram") && Boolean(prefs[ev.key]?.telegram)
    );
    if (tgRequired && telegramSelection.length === 0) errs.telegram = "empty";
    // Slack: same logic — if any event uses Slack, need at least one channel.
    const slackRequired = ALL_EVENTS.some(
      (ev) => !isChannelLocked(ev.key, "slack") && Boolean(prefs[ev.key]?.slack)
    );
    if (slackRequired && slackSelection.length === 0) errs.slack = "empty";
    return errs;
  }, [prefs, recipients, telegramSelection, slackSelection]);

  const hasAnyError = useMemo(
    () => Object.keys(channelErrors).length > 0,
    [channelErrors]
  );

  // ----- Save -------------------------------------------------------------
  // Backend still expects `_by_section` payloads. We mirror the single
  // unified recipient list into every section key whose events actually
  // have that channel enabled — sections with no enabled event get an
  // empty array. That keeps the backend agnostic of the UI merge.
  const handleSave = async () => {
    // Read-only teammates: revert local edits and surface the shared
    // permission modal instead of attempting the PUT (which would 403).
    if (!guardWrite(() => handleDiscardChanges())) return;
    setSaveAttempted(true);
    if (hasAnyError) {
      return t(
        'notification_center_validation_error',
        'Please fix the highlighted recipient fields before saving.'
      );
    }
    setSaving(true);
    try {
      const body = { prefs };
      for (const ch of RECIPIENT_CHANNELS) {
        const cleaned = (recipients[ch.key] || [])
          .map((e) => String(e).trim())
          .filter((e) => e.length > 0);
        const bySection = {};
        for (const sk of ALL_SECTION_KEYS) {
          const section = NOTIFICATION_SECTIONS.find((s) => s.key === sk);
          const sectionUsesChannel = section.events.some(
            (ev) => !isChannelLocked(ev.key, ch.key) && Boolean(prefs[ev.key]?.[ch.key])
          );
          bySection[sk] = sectionUsesChannel ? cleaned.slice() : [];
        }
        body[ch.payloadKey] = bySection;
      }
      const cleanedTelegram = telegramSelection.map((e) => String(e));
      const tgBySection = {};
      for (const sk of ALL_SECTION_KEYS) {
        const section = NOTIFICATION_SECTIONS.find((s) => s.key === sk);
        const sectionUsesTg = section.events.some(
          (ev) => !isChannelLocked(ev.key, "telegram") && Boolean(prefs[ev.key]?.telegram)
        );
        tgBySection[sk] = sectionUsesTg ? cleanedTelegram.slice() : [];
      }
      body[TELEGRAM_PAYLOAD_KEY] = tgBySection;

      const cleanedSlack = slackSelection.map((e) => String(e));
      const slackBySection = {};
      for (const sk of ALL_SECTION_KEYS) {
        const section = NOTIFICATION_SECTIONS.find((s) => s.key === sk);
        const sectionUsesSlack = section.events.some(
          (ev) => !isChannelLocked(ev.key, "slack") && Boolean(prefs[ev.key]?.slack)
        );
        slackBySection[sk] = sectionUsesSlack ? cleanedSlack.slice() : [];
      }
      body[SLACK_PAYLOAD_KEY] = slackBySection;
      body.usage_warn_threshold = usageThreshold;

      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}notification_settings/${botId}`,
        management.current.request(botId, body)
      );
      await managementSaved(res, management.current, botId, t);

      setInitialPrefs(prefs);
      const nextRecipients = {};
      for (const ch of RECIPIENT_CHANNELS) {
        nextRecipients[ch.key] = normalizeRecipientList(body[ch.payloadKey]);
      }
      setRecipients(nextRecipients);
      setInitialRecipients(nextRecipients);
      setTelegramSelection(normalizeTelegramSelection(tgBySection));
      setInitialTelegramSelection(normalizeTelegramSelection(tgBySection));
      setSlackSelection(normalizeTelegramSelection(slackBySection));
      setInitialSlackSelection(normalizeTelegramSelection(slackBySection));
      setInitialUsageThreshold(usageThreshold);
      setSaveAttempted(false);
      try {
        window.dispatchEvent(new CustomEvent("live-chat-prefs-updated", { detail: { botId } }));
      } catch (_e) { /* noop */ }
      return true;
    } catch (err) {
      console.error("Error saving notification prefs:", err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setPrefs(initialPrefs);
    setRecipients(initialRecipients);
    setTelegramSelection(initialTelegramSelection);
    setSlackSelection(initialSlackSelection);
    setUsageThreshold(initialUsageThreshold);
    setSaveAttempted(false);
  };

  useEffect(() => {
    if (onUnsavedStateChange) onUnsavedStateChange(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedStateChange]);

  // Keep latest handlers in a ref so guard registration doesn't re-run on every keystroke.
  const unsavedHandlersRef = useRef({ save: handleSave, discard: handleDiscardChanges });
  unsavedHandlersRef.current = { save: handleSave, discard: handleDiscardChanges };

  useEffect(() => {
    if (!onUnsavedHandlersChange) return undefined;
    onUnsavedHandlersChange({
      save: (...args) => unsavedHandlersRef.current.save(...args),
      discard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
    return () => onUnsavedHandlersChange(null);
  }, [onUnsavedHandlersChange]);

  useEffect(() => {
    return registerUnsavedChangesGuard(`notification-center-${botId}`, {
      when: hasUnsavedChanges,
      onSave: (...args) => unsavedHandlersRef.current.save(...args),
      onDiscard: (...args) => unsavedHandlersRef.current.discard(...args),
    });
  }, [botId, hasUnsavedChanges, registerUnsavedChangesGuard]);

  // ----- Error banner copy -----------------------------------------------
  const errorBanner = useMemo(() => {
    if (!hasAnyError || !saveAttempted) return null;
    const lines = [];
    for (const ch of RECIPIENT_CHANNELS) {
      if (channelErrors[ch.key]) lines.push(t(ch.validationErrorKey));
    }
    if (channelErrors.telegram) lines.push(t("notify_telegram_validation_error"));
    if (channelErrors.slack) lines.push(
      t("notify_slack_validation_error") ||
        "Pick at least one Slack channel for the notifications you've enabled."
    );
    return lines;
  }, [hasAnyError, saveAttempted, channelErrors, t]);

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="notification-center-parent-container">
      <div className="notification-center-container">
        <h4
          className="widget-sub-header"
          style={{ margin: 0, marginBottom: "16px", borderBottom: "none", paddingBottom: 0 }}
        >
          {t("notify_section_all") || "Notifications"}
        </h4>

        <Box className="control-group" sx={{ marginTop: 0, marginBottom: 0 }}>
          {/* Unified on/off matrix — every event in one place */}
          <div className="live-notify-matrix">
            <div className="live-notify-matrix-row live-notify-matrix-header">
              <div className="live-notify-matrix-event-col" />
              {NOTIFICATION_CHANNELS.map((ch) => (
                <div key={ch.key} className="live-notify-matrix-channel-col">
                  {ch.BrandIcon ? (
                    <span className="live-notify-matrix-channel-icon live-notify-matrix-channel-icon-brand">
                      <ch.BrandIcon size="16px" />
                    </span>
                  ) : (
                    <FontAwesomeIcon
                      icon={ch.icon}
                      className="live-notify-matrix-channel-icon"
                      style={ch.color ? { color: ch.color } : undefined}
                    />
                  )}
                  <span className="live-notify-matrix-channel-label-row">
                    <span className="live-notify-matrix-channel-label">
                      {t(ch.labelKey) || ch.key}
                    </span>
                    {ch.comingSoon && (
                      <span className="live-notify-matrix-coming-soon">
                        {t("live_notify_pref_coming_soon") || "Soon"}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {ALL_EVENTS.map((ev) => {
              const row = prefs[ev.key] || {};
              return (
                <div
                  key={ev.key}
                  className={`live-notify-matrix-row ${
                    ev.highlight ? "live-notify-matrix-row-highlight" : ""
                  }`}
                >
                  <div className="live-notify-matrix-event-col">
                    <div className="live-notify-matrix-event-title">
                      <FontAwesomeIcon
                        icon={ev.icon}
                        className="live-notify-matrix-event-icon"
                      />
                      {t(ev.titleKey) || ev.key}
                    </div>
                    {ev.key === "usage_warning" ? (
                      /* The threshold sentence IS this row's description line,
                         so the row keeps the same title + one-line height as
                         every other matrix row. */
                      <div className="live-notify-matrix-event-desc live-notify-usage-threshold">
                        <span>{t("notify_usage_threshold_label") || "Warn at"}</span>
                        <TextField
                          select
                          variant="standard"
                          value={usageThreshold}
                          onChange={(e) => {
                            setSaveAttempted(false);
                            setUsageThreshold(Number(e.target.value));
                          }}
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            "& .MuiInputBase-root": {
                              fontSize: "12px",
                              lineHeight: 1.4,
                              fontWeight: 600,
                              color: "#5616ea",
                              backgroundColor: "rgba(86, 22, 234, 0.06)",
                              border: "1px solid rgba(86, 22, 234, 0.35)",
                              borderRadius: "6px",
                              transition: "background-color 120ms ease, border-color 120ms ease",
                              "&:hover": {
                                backgroundColor: "rgba(86, 22, 234, 0.12)",
                                borderColor: "#5616ea",
                              },
                            },
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              height: "18px",
                              lineHeight: "18px",
                              paddingTop: 0,
                              paddingBottom: 0,
                              paddingLeft: "8px",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: "16px",
                              top: "calc(50% - 8px)",
                              right: "4px",
                              color: "#5616ea",
                            },
                          }}
                        >
                          {USAGE_THRESHOLD_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}%
                            </MenuItem>
                          ))}
                        </TextField>
                        <span>
                          {t("notify_usage_threshold_suffix") || "of monthly AI responses"}
                        </span>
                      </div>
                    ) : (
                      <div className="live-notify-matrix-event-desc">
                        {t(ev.descKey) || ""}
                      </div>
                    )}
                  </div>
                  {NOTIFICATION_CHANNELS.map((ch) => {
                    const locked = isChannelLocked(ev.key, ch.key) || Boolean(ch.comingSoon);
                    const checked = locked ? false : Boolean(row[ch.key]);
                    const checkbox = (
                      <Checkbox
                        checked={checked}
                        disabled={locked}
                        onChange={() => togglePref(ev.key, ch.key)}
                        sx={{
                          "&.Mui-checked": { color: "#5616ea" },
                          "&:hover": { backgroundColor: "#5616ea15" },
                          "&.Mui-disabled": { color: "#e0e0e6" },
                          padding: "4px",
                        }}
                      />
                    );
                    return (
                      <div key={ch.key} className="live-notify-matrix-channel-col">
                        {locked ? (
                          <Tooltip
                            title={
                              ch.comingSoon
                                ? (t("live_notify_pref_channel_coming_soon_tooltip") ||
                                    "This channel is coming soon")
                                : (t("live_notify_pref_channel_locked") ||
                                    "Not available for this notification type")
                            }
                            arrow
                          >
                            <span style={{ cursor: "not-allowed", display: "inline-flex" }}>
                              <span style={{ pointerEvents: "none" }}>{checkbox}</span>
                            </span>
                          </Tooltip>
                        ) : (
                          checkbox
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Per-channel recipient blocks (one shared list per channel) */}
          {(() => {
            const visibleRecipientChannels = RECIPIENT_CHANNELS.filter((ch) =>
              ALL_EVENTS.some(
                (ev) => !isChannelLocked(ev.key, ch.key) && Boolean(prefs[ev.key]?.[ch.key])
              )
            );
            const telegramVisible = ALL_EVENTS.some(
              (ev) => !isChannelLocked(ev.key, "telegram") && Boolean(prefs[ev.key]?.telegram)
            );
            const slackVisible = ALL_EVENTS.some(
              (ev) => !isChannelLocked(ev.key, "slack") && Boolean(prefs[ev.key]?.slack)
            );
            if (visibleRecipientChannels.length === 0 && !telegramVisible && !slackVisible) return null;
            return (
              <div className="notification-recipients-block">
                <h4
                  className="widget-sub-header"
                  style={{ margin: 0, marginBottom: "16px", borderBottom: "none", paddingBottom: 0 }}
                >
                  {t("notify_section_recipients") || "Recipients"}
                </h4>
                {visibleRecipientChannels.map((ch) => (
                  <RecipientListBlock
                    key={ch.key}
                    channel={ch}
                    values={recipients[ch.key] || [""]}
                    onChange={updateRecipient(ch.key)}
                    onAdd={addRecipient(ch.key)}
                    onRemove={removeRecipient(ch.key)}
                    saveAttempted={saveAttempted}
                    t={t}
                  />
                ))}
                {slackVisible && (
                  <SlackNotificationLinker
                    selectedDestinations={slackSelection}
                    onToggleDestination={toggleSlackDestination}
                  />
                )}
                {telegramVisible && (
                  <TelegramLinker
                    sectionKey="all"
                    links={telegramLinks}
                    selectedChatIds={telegramSelection}
                    onToggleChat={toggleTelegramChat}
                    onLinksChanged={fetchTelegramLinks}
                  />
                )}
              </div>
            );
          })()}
        </Box>

        {hasUnsavedChanges && (
          <div className="unsaved-changes-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">{t("unsaved_changes_info")}</div>
          </div>
        )}
        {errorBanner && errorBanner.length > 0 && (
          <div className="unsaved-changes-message notification-email-error-message">
            <div className="warning-icon">⚠️</div>
            <div className="warning-text">
              {errorBanner.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}
        <div className="save-discard-buttons">
          <button
            className="save-widget-btn"
            onClick={handleSave}
            disabled={saving || (saveAttempted && hasAnyError)}
          >
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : t("save")}
          </button>
          {hasUnsavedChanges && (
            <button onClick={handleDiscardChanges} className="discard-widget-btn">
              {t("discard_changes")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
